// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import Authentication from '@app/features/auth/state/Authentication';
import Channels from '@app/features/channel/state/Channels';
import * as GuildOnboardingCommands from '@app/features/guild/commands/GuildOnboardingCommands';
import styles from '@app/features/guild/components/modals/GuildOnboardingModal.module.css';
import GuildOnboardingStore from '@app/features/guild/state/GuildOnboarding';
import Guilds from '@app/features/guild/state/Guilds';
import {getInitialsFromName} from '@app/features/guild/utils/GuildInitialsUtils';
import GuildMembers from '@app/features/member/state/GuildMembers';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Spinner} from '@app/features/ui/components/Spinner';
import type {
	GuildOnboardingPrompt,
	GuildOnboardingResponse,
} from '@fluxer/schema/src/domains/guild/GuildOnboardingSchemas';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {CheckIcon, HashIcon, ShieldIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useMemo, useState} from 'react';

const WELCOME_TO_DESCRIPTOR = msg({
	message: 'Welcome to {guildName}',
	comment: 'Welcome heading in guild onboarding flow.',
});

const CUSTOMIZE_DESCRIPTOR = msg({
	message: 'Channels & Roles',
	comment: 'Title when reviewing or changing onboarding preferences.',
});

const COMPLETED_TOAST_DESCRIPTOR = msg({
	message: 'Welcome! Your roles and channels are set up.',
	comment: 'Toast when user finishes onboarding.',
});

interface GuildOnboardingModalProps {
	guildId: string;
	isCustomize?: boolean;
}

export const GuildOnboardingModal: React.FC<GuildOnboardingModalProps> = observer(
	({guildId, isCustomize = false}) => {
		const {i18n} = useLingui();
		const guild = Guilds.getGuild(guildId);
		const currentUserId = Authentication.currentUserId;
		const member = GuildMembers.getMember(guildId, currentUserId);

		const [loading, setLoading] = useState(true);
		const [submitting, setSubmitting] = useState(false);
		const [onboarding, setOnboarding] = useState<GuildOnboardingResponse | null>(null);
		const [selectedOptionIds, setSelectedOptionIds] = useState<Set<string>>(new Set());

		const guildChannels = useMemo(() => Channels.getGuildChannels(guildId), [guildId]);
		const channelMap = useMemo(() => new Map(guildChannels.map((c) => [c.id, c.name])), [guildChannels]);
		const roleMap = useMemo(() => {
			if (!guild) return new Map();
			return new Map(Object.values(guild.roles).map((r) => [r.id, r.name]));
		}, [guild]);

		useEffect(() => {
			let isCancelled = false;
			const load = async () => {
				setLoading(true);
				try {
					const data = await GuildOnboardingStore.fetchOnboarding(guildId);
					if (isCancelled || !data) return;
					setOnboarding(data);

					// Pre-select options matching member's current roles if in customize mode
					const initialSelected = new Set<string>();
					if (member) {
						for (const prompt of data.prompts) {
							for (const option of prompt.options) {
								if (option.role_ids && option.role_ids.length > 0) {
									const hasAllRoles = option.role_ids.every((rId) => member.roles.has(rId));
									if (hasAllRoles) {
										initialSelected.add(option.id);
									}
								}
							}
						}
					}
					setSelectedOptionIds(initialSelected);
				} catch {
					ToastCommands.error('Could not load onboarding questions');
				} finally {
					if (!isCancelled) setLoading(false);
				}
			};
			void load();
			return () => {
				isCancelled = true;
			};
		}, [guildId, member]);

		const handleToggleOption = useCallback(
			(prompt: GuildOnboardingPrompt, optionId: string) => {
				setSelectedOptionIds((prev) => {
					const next = new Set(prev);
					if (prompt.single_select) {
						// Deselect any other options from this same prompt
						for (const opt of prompt.options) {
							next.delete(opt.id);
						}
						// Toggle this option
						if (!prev.has(optionId)) {
							next.add(optionId);
						}
					} else {
						if (next.has(optionId)) {
							next.delete(optionId);
						} else {
							next.add(optionId);
						}
					}
					return next;
				});
			},
			[],
		);

		const handleFinish = useCallback(async () => {
			if (!onboarding) return;

			// Check required prompts
			for (const prompt of onboarding.prompts) {
				if (prompt.required) {
					const hasAnswer = prompt.options.some((opt) => selectedOptionIds.has(opt.id));
					if (!hasAnswer) {
						ToastCommands.error(`Please answer: "${prompt.title}"`);
						return;
					}
				}
			}

			setSubmitting(true);
			try {
				await GuildOnboardingCommands.submitGuildOnboardingResponses(guildId, {
					onboarding_responses: Array.from(selectedOptionIds),
				});
				ModalCommands.pop();
				ToastCommands.success(i18n._(COMPLETED_TOAST_DESCRIPTOR));
			} catch (error) {
				ToastCommands.error('Could not save your choices');
			} finally {
				setSubmitting(false);
			}
		}, [guildId, onboarding, selectedOptionIds, i18n]);

		const handleSkip = useCallback(() => {
			GuildOnboardingStore.dismissForSession(guildId);
			ModalCommands.pop();
		}, [guildId]);

		const displayedPrompts = useMemo(() => {
			if (!onboarding) return [];
			if (isCustomize) return onboarding.prompts;
			return onboarding.prompts.filter((p) => p.in_onboarding);
		}, [onboarding, isCustomize]);

		return (
			<Modal.Root size="medium" centered data-flx="guild.guild-onboarding-modal.root">
				<Modal.Content data-flx="guild.guild-onboarding-modal.content" style={{padding: 0, overflow: 'hidden'}}>
					<div className={styles.headerBanner} data-flx="guild.guild-onboarding-modal.header-banner">
						<div className={styles.guildIconWrap} data-flx="guild.guild-onboarding-modal.guild-icon">
							{guild?.icon ? (
								<img
									src={`/instance/assets/icons/${guild.id}/${guild.icon}.webp`}
									alt={guild.name}
									className={styles.guildIconImg}
								/>
							) : (
								<span className={styles.guildInitials}>{getInitialsFromName(guild?.name ?? '')}</span>
							)}
						</div>
						<div className={styles.welcomeTitle} data-flx="guild.guild-onboarding-modal.title">
							{isCustomize
								? i18n._(CUSTOMIZE_DESCRIPTOR)
								: i18n._(WELCOME_TO_DESCRIPTOR, {guildName: guild?.name ?? 'the server'})}
						</div>
						<div className={styles.welcomeSubtitle}>
							{isCustomize
								? 'Pick the questions and roles that match your interests. You can come back and change this anytime.'
								: 'Answer a few questions to get assigned relevant roles and discover channels!'}
						</div>
					</div>

					{loading ? (
						<div style={{display: 'flex', justifyContent: 'center', padding: '3rem 0'}}>
							<Spinner />
						</div>
					) : (
						<div className={styles.contentScroll} data-flx="guild.guild-onboarding-modal.scroll">
							{displayedPrompts.map((prompt) => (
								<div key={prompt.id} className={styles.questionCard} data-flx="guild.guild-onboarding-modal.question">
									<div className={styles.questionHeader}>
										<span className={styles.questionTitle}>{prompt.title}</span>
										{prompt.required && <span className={clsx(styles.badge, styles.requiredBadge)}>Required</span>}
									</div>

									<div className={styles.optionsGrid}>
										{prompt.options.map((opt) => {
											const isSelected = selectedOptionIds.has(opt.id);
											return (
												<div
													key={opt.id}
													className={clsx(styles.optionCard, isSelected && styles.optionCardSelected)}
													onClick={() => handleToggleOption(prompt, opt.id)}
													data-flx="guild.guild-onboarding-modal.option-card"
												>
													<div className={styles.optionMain}>
														<div className={styles.optionTitle}>{opt.title}</div>
														{opt.description && <div className={styles.optionDescription}>{opt.description}</div>}
														<div className={styles.optionPillsRow}>
															{(opt.role_ids || []).map((rId) => {
																const name = roleMap.get(rId);
																if (!name) return null;
																return (
																	<span key={rId} className={styles.pill}>
																		<ShieldIcon size={10} />
																		<span>{name}</span>
																	</span>
																);
															})}
															{(opt.channel_ids || []).map((cId) => {
																const name = channelMap.get(cId);
																if (!name) return null;
																return (
																	<span key={cId} className={styles.pill}>
																		<HashIcon size={10} />
																		<span>{name}</span>
																	</span>
																);
															})}
														</div>
													</div>

													<div
														className={clsx(
															styles.checkboxCircle,
															isSelected && styles.checkboxCircleSelected,
														)}
													>
														{isSelected && <CheckIcon size={14} weight="bold" />}
													</div>
												</div>
											);
										})}
									</div>
								</div>
							))}
						</div>
					)}
				</Modal.Content>

				<Modal.Footer data-flx="guild.guild-onboarding-modal.footer">
					<div className={styles.footer}>
						{!isCustomize ? (
							<Button variant="secondary" onClick={handleSkip} data-flx="guild.guild-onboarding-modal.skip">
								Skip for now
							</Button>
						) : (
							<div />
						)}
						<Button
							variant="primary"
							onClick={handleFinish}
							disabled={loading || submitting}
							submitting={submitting}
							data-flx="guild.guild-onboarding-modal.finish"
						>
							{isCustomize ? 'Save Preferences' : 'Finish & Join'}
						</Button>
					</div>
				</Modal.Footer>
			</Modal.Root>
		);
	},
);

export default GuildOnboardingModal;
